/**
 * Audit Criteria — native taxonomy layer
 *
 * interfaceaudit.com presents its evaluation framework as "Audit Criteria":
 * native, audit-oriented categories with native codes (e.g. NAV-03).
 *
 * This layer maps the internal principle dataset onto a clean, product-native
 * taxonomy. The internal `code`/`slug`/`part`/`chapter` identifiers are NOT
 * surfaced anywhere user-facing; only `auditCode` + category are. Native codes
 * are derived deterministically by AuditCriteriaService.
 */

import type { AuditCategoryId, AuditCategoryMeta, AuditEvidenceType } from '@/types/audit';

/**
 * The twelve native audit categories, in reading order.
 * Web-audit dimensions first; emerging/AI interfaces last.
 */
export const AUDIT_CATEGORIES: AuditCategoryMeta[] = [
  {
    id: 'FND',
    prefix: 'FND',
    order: 1,
    label: { en: 'Foundations & Psychology', es: 'Fundamentos y Psicología' },
    blurb: {
      en: 'How people perceive, decide, and behave — the cognitive basis for usable interfaces.',
      es: 'Cómo las personas perciben, deciden y se comportan: la base cognitiva de una interfaz usable.',
    },
    evidence: {
      type: 'research',
      basis: {
        en: 'Cognitive psychology and human-computer interaction research: perception, attention, decision making, motivation.',
        es: 'Investigación en psicología cognitiva e interacción humano-computadora: percepción, atención, toma de decisiones, motivación.',
      },
    },
  },
  {
    id: 'STD',
    prefix: 'STD',
    order: 2,
    label: { en: 'Consistency & Standards', es: 'Consistencia y Estándares' },
    blurb: {
      en: 'Conventions and a coherent system that keep the interface predictable.',
      es: 'Convenciones y un sistema coherente que mantienen la interfaz predecible.',
    },
    evidence: {
      type: 'heuristic',
      basis: {
        en: 'Usability heuristics (consistency and standards) and platform conventions.',
        es: 'Heurísticas de usabilidad (consistencia y estándares) y convenciones de plataforma.',
      },
    },
  },
  {
    id: 'VIS',
    prefix: 'VIS',
    order: 3,
    label: { en: 'Visual & Data Design', es: 'Diseño Visual y de Datos' },
    blurb: {
      en: 'Visual hierarchy, layout, and data presentation that guide attention.',
      es: 'Jerarquía visual, maquetación y presentación de datos que guían la atención.',
    },
    evidence: {
      type: 'research',
      basis: {
        en: 'Visual perception research (Gestalt, hierarchy, contrast) and data-visualization practice.',
        es: 'Investigación en percepción visual (Gestalt, jerarquía, contraste) y práctica de visualización de datos.',
      },
    },
  },
  {
    id: 'CONT',
    prefix: 'CONT',
    order: 4,
    label: { en: 'Content & Clarity', es: 'Contenido y Claridad' },
    blurb: {
      en: 'Clear content, sound information organization, and help where users need it.',
      es: 'Contenido claro, organización de la información y ayuda donde el usuario la necesita.',
    },
    evidence: {
      type: 'heuristic',
      basis: {
        en: 'Usability heuristics (aesthetic and minimalist design, help and documentation) and content-design practice.',
        es: 'Heurísticas de usabilidad (diseño estético y minimalista, ayuda y documentación) y práctica de diseño de contenido.',
      },
    },
  },
  {
    id: 'NAV',
    prefix: 'NAV',
    order: 5,
    label: { en: 'Navigation & Search', es: 'Navegación y Búsqueda' },
    blurb: {
      en: 'Wayfinding, navigation structure, and findable search.',
      es: 'Orientación, estructura de navegación y búsqueda localizable.',
    },
    evidence: {
      type: 'pattern',
      basis: {
        en: 'Information-architecture practice and navigation conventions users already know.',
        es: 'Práctica de arquitectura de información y convenciones de navegación que los usuarios ya conocen.',
      },
    },
  },
  {
    id: 'INT',
    prefix: 'INT',
    order: 6,
    label: { en: 'Interaction Patterns', es: 'Patrones de Interacción' },
    blurb: {
      en: 'Interaction patterns and interface elements that respond as users expect.',
      es: 'Patrones de interacción y elementos de interfaz que responden como el usuario espera.',
    },
    evidence: {
      type: 'pattern',
      basis: {
        en: 'Established interaction design patterns and interface element conventions.',
        es: 'Patrones de diseño de interacción establecidos y convenciones de elementos de interfaz.',
      },
    },
  },
  {
    id: 'FB',
    prefix: 'FB',
    order: 7,
    label: { en: 'Feedback & System Status', es: 'Feedback y Estado del Sistema' },
    blurb: {
      en: 'Visible system status and timely feedback for every action.',
      es: 'Estado del sistema visible y feedback oportuno para cada acción.',
    },
    evidence: {
      type: 'heuristic',
      basis: {
        en: 'Nielsen\'s visibility-of-system-status heuristic and feedback research.',
        es: 'La heurística de visibilidad del estado del sistema de Nielsen e investigación sobre feedback.',
      },
    },
  },
  {
    id: 'ERR',
    prefix: 'ERR',
    order: 8,
    label: { en: 'Error Prevention & Recovery', es: 'Prevención y Recuperación de Errores' },
    blurb: {
      en: 'Preventing mistakes and helping users recover quickly when they happen.',
      es: 'Prevenir errores y ayudar a recuperarse rápido cuando ocurren.',
    },
    evidence: {
      type: 'heuristic',
      basis: {
        en: 'Nielsen\'s error-prevention and error-recovery heuristics.',
        es: 'Las heurísticas de prevención y recuperación de errores de Nielsen.',
      },
    },
  },
  {
    id: 'CTRL',
    prefix: 'CTRL',
    order: 9,
    label: { en: 'User Control & Efficiency', es: 'Control y Eficiencia' },
    blurb: {
      en: 'User control, freedom, and efficient shortcuts for novices and experts alike.',
      es: 'Control del usuario, libertad y atajos eficientes para novatos y expertos.',
    },
    evidence: {
      type: 'heuristic',
      basis: {
        en: 'Nielsen\'s user-control and flexibility heuristics.',
        es: 'Las heurísticas de control del usuario y flexibilidad de Nielsen.',
      },
    },
  },
  {
    id: 'MOB',
    prefix: 'MOB',
    order: 10,
    label: { en: 'Mobile & Performance', es: 'Móvil y Rendimiento' },
    blurb: {
      en: 'Mobile ergonomics, responsive behavior, and perceived performance.',
      es: 'Ergonomía móvil, comportamiento responsive y rendimiento percibido.',
    },
    evidence: {
      type: 'pattern',
      basis: {
        en: 'Platform guidance for touch interfaces (target size, spacing) and performance practice.',
        es: 'Guías de plataforma para interfaces táctiles (tamaño y separación de objetivos) y práctica de rendimiento.',
      },
    },
  },
  {
    id: 'ACC',
    prefix: 'ACC',
    order: 11,
    label: { en: 'Accessibility & Ethics', es: 'Accesibilidad y Ética' },
    blurb: {
      en: 'Accessible, inclusive, and ethical experiences for every user.',
      es: 'Experiencias accesibles, inclusivas y éticas para cada usuario.',
    },
    evidence: {
      type: 'standard',
      basis: {
        en: 'WCAG 2.1 AA success criteria and inclusive-design practice. The automated checks cover a subset of the standard, not a conformance evaluation.',
        es: 'Criterios de éxito WCAG 2.1 AA y práctica de diseño inclusivo. Los chequeos automatizados cubren un subconjunto del estándar, no una evaluación de conformidad.',
      },
    },
  },
  {
    id: 'AIX',
    prefix: 'AIX',
    order: 12,
    label: { en: 'AI & Emerging Interfaces', es: 'IA e Interfaces Emergentes' },
    blurb: {
      en: 'Emerging surfaces: AI, conversational, agentic, and spatial experiences.',
      es: 'Superficies emergentes: experiencias con IA, conversacionales, agénticas y espaciales.',
    },
    evidence: {
      type: 'emerging',
      basis: {
        en: 'Published human-AI interaction guidelines and emerging patterns for conversational, agentic and spatial interfaces.',
        es: 'Guías publicadas de interacción humano-IA y patrones emergentes para interfaces conversacionales, agénticas y espaciales.',
      },
    },
  },
];

/**
 * Evidence basis vocabulary, in the order the /criteria legend shows it.
 * Assigned per category (not per criterion): the criteria data carries no
 * source field, so a per-criterion classification would be invented. The
 * category-level basis is a reviewable editorial statement of where each
 * group of criteria comes from.
 */
export const AUDIT_EVIDENCE_TYPES: Record<AuditEvidenceType, { label: { en: string; es: string }; definition: { en: string; es: string } }> = {
  standard: {
    label: { en: 'Standard', es: 'Estándar' },
    definition: {
      en: 'A published standard with testable success criteria, such as WCAG 2.1 AA.',
      es: 'Un estándar publicado con criterios de éxito verificables, como WCAG 2.1 AA.',
    },
  },
  research: {
    label: { en: 'Established research', es: 'Investigación establecida' },
    definition: {
      en: 'Findings from cognitive psychology and human-computer interaction replicated over decades.',
      es: 'Hallazgos de psicología cognitiva e interacción humano-computadora replicados durante décadas.',
    },
  },
  heuristic: {
    label: { en: 'Heuristic', es: 'Heurística' },
    definition: {
      en: "Expert rules of thumb such as Nielsen's usability heuristics: widely used, grounded in practice rather than controlled studies.",
      es: 'Reglas de expertos como las heurísticas de usabilidad de Nielsen: de uso extendido, basadas en la práctica más que en estudios controlados.',
    },
  },
  pattern: {
    label: { en: 'Design pattern', es: 'Patrón de diseño' },
    definition: {
      en: 'Conventions that work because users have learned them: platform guidance and common interface patterns.',
      es: 'Convenciones que funcionan porque los usuarios las han aprendido: guías de plataforma y patrones de interfaz comunes.',
    },
  },
  emerging: {
    label: { en: 'Emerging guidance', es: 'Guía emergente' },
    definition: {
      en: 'Recent human-AI interaction guidelines and patterns: the youngest evidence in the framework, still being validated.',
      es: 'Guías y patrones recientes de interacción humano-IA: la evidencia más joven del marco, aún en validación.',
    },
  },
};

export const AUDIT_EVIDENCE_ORDER: AuditEvidenceType[] = ['standard', 'research', 'heuristic', 'pattern', 'emerging'];

/**
 * Maps each internal chapter name to a native category.
 * This is the core editorial mapping (44 chapters -> 12 categories).
 */
export const CHAPTER_TO_CATEGORY: Record<string, AuditCategoryId> = {
  // Foundations & Psychology
  'Cognitive Psychology & Perception': 'FND',
  'Decision Making & Behavior': 'FND',
  'Human Behavior & Decision Making': 'FND',
  'Motivation & Engagement': 'FND',

  // Consistency & Standards
  'Fundamental Design Principles': 'STD',
  'System Integration': 'STD',

  // Visual & Data Design
  'Visual Design Principles': 'VIS',
  'Data Visualization': 'VIS',

  // Content & Clarity
  'Aesthetic & Minimalist': 'CONT',
  'Help & Documentation': 'CONT',
  'Content Organization': 'CONT',

  // Navigation & Search
  'Navigation and Wayfinding': 'NAV',
  'Search and Discovery': 'NAV',

  // Interaction Patterns
  'Interaction Design Patterns': 'INT',
  'Interface Elements': 'INT',

  // Feedback & System Status
  'Feedback & Communication': 'FB',

  // Error Prevention & Recovery
  'Error Communication': 'ERR',
  'Error Prevention & Recovery': 'ERR',

  // User Control & Efficiency
  'Efficiency & Flexibility': 'CTRL',
  'User Control & Freedom': 'CTRL',

  // Mobile & Performance
  'Mobile Design Patterns': 'MOB',
  'Performance & Technical UX': 'MOB',
  'Performance and Resilient Design': 'MOB',

  // Accessibility & Ethics
  'Accessibility and Inclusion': 'ACC',
  'Ethics and Responsibility': 'ACC',
  'Wellbeing and Empowerment': 'ACC',

  // AI & Emerging Interfaces
  'AI & Cognitive Psychology': 'AIX',
  'AI Transparency & Trust': 'AIX',
  'AI Design System Integration': 'AIX',
  'AI Interaction Patterns': 'AIX',
  'AI and Intelligent Interfaces': 'AIX',
  'AI in Creative Tools': 'AIX',
  'Agent Design Patterns': 'AIX',
  'Enterprise AI Patterns': 'AIX',
  'HAX During Phase': 'AIX',
  'HAX Error & Long-term': 'AIX',
  'HAX Initial Phase': 'AIX',
  'Shape of AI Governors': 'AIX',
  'Shape of AI Inputs': 'AIX',
  'Shape of AI Trust': 'AIX',
  'Shape of AI Wayfinders': 'AIX',
  'Spatial and Immersive Interfaces': 'AIX',
  'Voice and Conversational Interfaces': 'AIX',
  'Human-AI Collaboration': 'AIX',
};

/**
 * Fallback mapping by part name, used only if a chapter is unmapped.
 */
export const PART_TO_CATEGORY: Record<string, AuditCategoryId> = {
  'Part I - Foundations': 'FND',
  'Part II - Core Principles': 'STD',
  'Part III - Design Systems': 'VIS',
  'Part IV - Interface Patterns': 'INT',
  'Part V - Specialized Domains': 'AIX',
  'Part VI - Human-Centered AI': 'AIX',
  'Part VI - Human-Centered Excellence': 'ACC',
};
